import { FC, Suspense } from 'react';
import { useLocation, Link as RouterLink } from 'react-router';
import { Avatar, Box, Grid, IconButton, Link, List, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { useRouter } from '@cvt/routing';
import { useDictionary } from '@cvt/hooks/useDictionary';
import ErrorBoundary from '@cvt/components/ErrorBoundary';
import { PermissionedView } from '@cvt/components/Permissioned';
import { ListLoading } from '@cvt/components/layout/ListLoading';

import { useSpecialization } from '@modules/Specializations/hooks/useSpecialization';
import { SpecializationDetails } from '@modules/Specializations/partials/SpecializationDetails';
import { getSpecializationLevelLabel } from '@modules/Specializations/helpers/specialization';

import { ViewHeader } from '@shared/components/ViewHeader';

import { useUniversitySpecializationLevels } from '../../../../../../hooks/useUniversitySpecializationLevels';
import { InfiniteUniversitiesList } from '../../../../../../partials/InfiniteUniversitiesList';
import { UniversityRow, UniversityRowSkeleton } from '../../../../../../components/UniversityRow';
import { UniversityAdmissionsButton } from '../../../../../../components/UniversityAdmissionButton';
import { ViewUniversityAdmissionContacts } from '../../../../../../components/ViewUniversityAdmissionContacts';

interface ViewProps {
  university: Universities.University;
  major: Majors.Major;
  specialization: Specializations.Specialization;
}

export const ViewUniversityMajorSpecializationView: FC<ViewProps> = ({ university, major, specialization }) => {
  const dictionary = useDictionary();
  const router = useRouter();
  const location = useLocation();

  const { levels } = useUniversitySpecializationLevels({
    university: university.id,
    specialization: specialization.id,
  });

  return (
    <Stack direction="column">
      <Grid container spacing={2}>
        <Grid size={12}>
          <ViewHeader
            category={(
              <>
                <Link
                  underline="hover"
                  color="inherit"
                  component={RouterLink}
                  to={router.universities.view(university.id).path}
                >
                  {university.name}
                </Link>
                {' - '}
                <Link
                  underline="hover"
                  color="inherit"
                  component={RouterLink}
                  to={router.majors.view(major.id).path}
                >
                  {major.title}
                </Link>
              </>
            )}
            title={specialization.title}
            slots={{
              left: (
                <IconButton component={RouterLink} to={router.universities.view(university.id).majors.view(major.id).specializations.path} color="primary">
                  <ArrowBack/>
                </IconButton>
              ),
            }}
          />
          {levels.length > 0 && (
            <Box px={2} mb={3}>
              <Typography variant="subtitle1" mb={0.5} ml={1}>Available Degrees</Typography>
              <Paper variant="outlined" sx={theme => ({ backgroundColor: theme.palette.fourtiary.light })}>
                <List disablePadding>
                  {levels.map(level => (
                    <ListItem dense>
                      <ListItemText primary={getSpecializationLevelLabel(dictionary, level.level)} secondary={level.duration} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          )}
          <SpecializationDetails {...specialization} />
          <Box pt={3} pb={1}>
            <Box px={2}>
              <Typography variant="subtitle1" mb={0.5} ml={1}>Start your application to</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar src={university.logo} alt={university.name} />
                <Stack>
                  <Link underline="none" fontSize={14} component={RouterLink} to={router.universities.view(university.id).path} color="inherit">
                    <Typography variant="h1">{university.name}</Typography>
                  </Link>
                  <Link underline="hover" fontSize={14} component={RouterLink} to={router.universities.view(university.id).path}>View page</Link>
                </Stack>
              </Stack>
            </Box>
          </Box>
          <UniversityAdmissionsButton university={university} specialization={specialization} />
          <ViewUniversityAdmissionContacts {...university} />
          {/* UNIVERSITES */}
          <Box
            p={2}
            width="100%"
            sx={theme => ({ backgroundColor: theme.palette.tertiary.light })}
          >
            <Typography variant="subtitle1" mb={0.5}>Universities offering this specialization</Typography>
            <ErrorBoundary>
              <PermissionedView permission="universities.list">
                <Paper variant="outlined">
                  <Suspense key={location.key} fallback={(
                    <ListLoading
                      disablePadding
                      repeat={3}
                      renderItem={() => <UniversityRowSkeleton/>}
                    />
                  )}>
                    <InfiniteUniversitiesList
                      filters={{
                        specialization: specialization.id,
                        limit: 20,
                      }}
                      renderSkeleton={() => <UniversityRowSkeleton/>}
                      renderItem={university => <UniversityRow key={university.id} {...university} linkTo={router.universities.view(university.id).majors.view(major.id).specializations.view(specialization.id).path} />}
                    />
                  </Suspense>
                </Paper>
              </PermissionedView>
            </ErrorBoundary>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
};

interface Props {
  id: number;
  university: Universities.University;
  major: Majors.Major;
}

export const ViewUniversityMajorSpecialization: FC<Props> = ({ id, university, major }) => {

  const { specialization } = useSpecialization({
    id,
  });

  if (!specialization) {
    return null;
  }

  return (
    <ViewUniversityMajorSpecializationView university={university} major={major} specialization={specialization} />
  );
};
